import classNames from 'classnames';
import { Parchment } from 'quill/core';
import React, { useMemo } from 'react';
import DangerouslySetInnerHTML from '../components/DangerouslySetInnerHTML';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import safeParseUrl from '../utils/url/safeParseUrl';
import { QlEditor } from './ReactQuill';
import type { DraggableBlot } from './ReorderGutterButton';
import * as styles from './ReorderSnapshot.module.scss';
import { iframeIcon } from './controls/editorIcons';
import { CAROUSEL_IMAGE_ATTRIBUTE } from './image/ImageNode';
import { VIDEO_SOURCES } from './utils/matchVideo';

interface HTMLSnapshotProps {
  html: string;
  bordered?: boolean;
}

const HTMLSnapshot = ({ html, bordered }: HTMLSnapshotProps) => (
  <div className={classNames({ [styles.borderedContainer]: bordered })}>
    <DangerouslySetInnerHTML html={html}>
      <QlEditor className="reorder-dragging" />
    </DangerouslySetInnerHTML>
  </div>
);

interface ReorderSnapshotProps {
  blots: DraggableBlot[];
}

const ReorderSnapshot = ({ blots }: ReorderSnapshotProps) => {
  const node = useMemo(() => {
    if (blots[0] instanceof Parchment.LeafBlot) {
      const { blotName } = blots[0].statics;
      const value = blots[0].value()[blotName];

      switch (blotName) {
        case 'embed':
        case 'iframe':
        case 'video': {
          let icon = null;
          if (blotName === 'video') {
            const url = safeParseUrl(value);
            const source =
              url &&
              Object.values(VIDEO_SOURCES).find(
                ({ formatters: { embedUrl } }) => embedUrl.testHost(url.host),
              );
            if (source) {
              icon = <ExternalBrandLogo height={16} {...source.iconProps} />;
            }
          } else if (blotName === 'embed') {
            const [brand, product] = value.type.split('-');
            icon = (
              <ExternalBrandLogo height={16} brand={brand} product={product} />
            );
          } else {
            icon = <SVGIcon size={16}>{iframeIcon}</SVGIcon>;
          }
          return (
            <div
              className={classNames(
                styles.borderedContainer,
                styles.embedContainer,
              )}
            >
              {icon}
            </div>
          );
        }
        case 'image': {
          const img = blots[0].domNode
            .querySelector(`[${CAROUSEL_IMAGE_ATTRIBUTE}]`)
            ?.cloneNode();
          if (img instanceof HTMLImageElement) {
            img.classList.add(styles.draggedImageGhost);
            return <HTMLSnapshot html={img.outerHTML} />;
          }

          return <HTMLSnapshot html={blots[0].domNode.outerHTML} />;
        }
        default:
          return <HTMLSnapshot html={blots[0].domNode.outerHTML} />;
      }
    }

    return (
      <HTMLSnapshot
        bordered
        html={`<ol>${blots
          .map(({ domNode }) => domNode.outerHTML)
          .join('')}</ol>`}
      />
    );
  }, [blots]);

  return node;
};

export default ReorderSnapshot;
